import React, { useState } from "react";

/**
 * Immutably removes a rubric (by id) from anywhere inside a rubric tree's
 * `children`. Used to keep already-fetched trees in sync after a delete
 * without having to refetch the whole tree.
 */
export const removeFromTree = (node, id) => {
  if (!node) return node;
  const children = (node.children || [])
    .filter((child) => String(child.id) !== String(id))
    .map((child) => removeFromTree(child, id));
  return { ...node, children };
};

/** Same as removeFromTree, but for a flat array of root rubric nodes. */
export const removeFromForest = (nodes, id) =>
  (nodes || [])
    .filter((node) => String(node.id) !== String(id))
    .map((node) => removeFromTree(node, id));

const RubricTreeNode = ({ node, depth, deletingIds, onDelete }) => {
  const [open, setOpen] = useState(depth < 1);
  const isDeleting = !!deletingIds[node.id];
  const hasChildren = node.children?.length > 0;

  return (
    <div
      className={depth > 0 ? "ps-3 border-start" : ""}
      style={{ opacity: isDeleting ? 0.5 : 1, marginTop: depth === 0 ? 0 : 8 }}
    >
      <div className="d-flex align-items-center gap-2 py-1">
        {hasChildren ? (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-decoration-none"
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? "Collapse" : "Expand"}
          >
            {open ? "▼" : "▶"}
          </button>
        ) : (
          <span style={{ width: "1em" }} />
        )}

        <span className="fw-semibold">{node.name}</span>

        {hasChildren && (
          <span className="badge text-bg-light border">
            {node.children.length} sub-rubric{node.children.length === 1 ? "" : "s"}
          </span>
        )}

        <button
          type="button"
          className="btn btn-sm btn-outline-danger ms-auto"
          onClick={() => onDelete(node.id)}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
              />
              Deleting…
            </>
          ) : (
            "Delete"
          )}
        </button>
      </div>

      {node.notes && <p className="text-muted small mb-1">{node.notes}</p>}

      {node.remedies?.length > 0 && (
        <div className="mb-1 d-flex flex-wrap gap-1">
          {node.remedies.map((r, i) => (
            <span
              key={i}
              className={`badge rounded-pill ${
                r.weight >= 3
                  ? "text-bg-success"
                  : r.weight >= 2
                  ? "text-bg-info"
                  : "text-bg-secondary"
              }`}
              title={`weight ${r.weight}`}
            >
              {r.name}
              <sup className="ms-1">{r.weight}</sup>
            </span>
          ))}
        </div>
      )}

      {open &&
        hasChildren &&
        node.children.map((child) => (
          <RubricTreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            deletingIds={deletingIds}
            onDelete={onDelete}
          />
        ))}
    </div>
  );
};

export default RubricTreeNode;
